import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, Image as ImageIcon, FileType2, Download, Loader2, User, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";

export interface SharedNote {
  id: string;
  title: string;
  description: string | null;
  education_level: string;
  category: string;
  semester: string | null;
  subject: string;
  chapter: string | null;
  file_path: string;
  file_type: string;
  file_size_bytes: number;
  downloads: number;
  uploader_name: string;
  uploader_id: string;
  created_at: string;
}

const iconFor = (t: string) => {
  if (t === "pdf") return <FileText className="w-5 h-5" />;
  if (t === "docx" || t === "doc") return <FileType2 className="w-5 h-5" />;
  return <ImageIcon className="w-5 h-5" />;
};

const formatSize = (b: number) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

interface Props {
  note: SharedNote;
  onDeleted?: (id: string) => void;
}

const SharedNoteCard = ({ note, onDeleted }: Props) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isOwner = user?.id === note.uploader_id;

  const handleDownload = async () => {
    if (!user) {
      navigate("/auth");
      return;
    }
    setDownloading(true);
    try {
      const { data, error } = await supabase.storage
        .from("shared-notes")
        .createSignedUrl(note.file_path, 60);
      if (error || !data) throw error || new Error("Failed to get download link");
      await supabase.rpc("increment_note_download" as any, { note_id: note.id });
      window.open(data.signedUrl, "_blank", "noopener,noreferrer");
    } catch (e) {
      console.error(e);
      toast({ title: "Download failed", description: "Please try again.", variant: "destructive" });
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    if (!isOwner) return;
    if (!confirm(`Delete "${note.title}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const { error } = await supabase.from("shared_notes" as any).delete().eq("id", note.id);
      if (error) throw error;
      await supabase.storage.from("shared-notes").remove([note.file_path]);
      toast({ title: "Deleted", description: "Your notes have been removed." });
      onDeleted?.(note.id);
    } catch (e) {
      console.error(e);
      toast({ title: "Delete failed", description: "Please try again.", variant: "destructive" });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <article className="glass rounded-2xl p-5 flex flex-col gap-3 hover:shadow-glow transition-all">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
          {iconFor(note.file_type)}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-foreground break-words leading-snug">{note.title}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {format(new Date(note.created_at), "MMM d, yyyy")} · {formatSize(note.file_size_bytes)}
          </p>
        </div>
      </div>

      {note.description && (
        <p className="text-sm text-muted-foreground line-clamp-2 break-words">{note.description}</p>
      )}

      <div className="flex flex-wrap gap-1.5">
        <Badge variant="secondary" className="capitalize">{note.education_level}</Badge>
        <Badge variant="outline">{note.category}</Badge>
        {note.semester && <Badge variant="outline">{note.semester}</Badge>}
        <Badge variant="outline">{note.subject}</Badge>
        {note.chapter && <Badge variant="outline" className="max-w-[180px] truncate">{note.chapter}</Badge>}
      </div>

      <div className="flex items-center gap-2 pt-3 mt-auto border-t border-border/40">
        <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <User className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-muted-foreground leading-none">Uploaded by</p>
          <p className="text-sm font-semibold text-foreground truncate">{note.uploader_name}</p>
        </div>
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {note.downloads} {note.downloads === 1 ? "download" : "downloads"}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="hero"
          onClick={handleDownload}
          disabled={downloading || deleting}
          className="flex-1"
          aria-label={`Download ${note.title}`}
        >
          {downloading ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Preparing…</>
          ) : (
            <><Download className="w-4 h-4 mr-2" /> Download notes</>
          )}
        </Button>
        {isOwner && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleDelete}
            disabled={deleting || downloading}
            aria-label={`Delete ${note.title}`}
            className="shrink-0"
          >
            {deleting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4 text-destructive" />
            )}
          </Button>
        )}
      </div>
    </article>
  );
};

export default SharedNoteCard;
